import axios from 'axios'

/**
 * Error normalizado de la API.
 *
 * ¿Por qué no usar el error de Axios directamente? Porque un fallo de red puede
 * llegar de tres formas muy distintas: el servidor respondió con 400, el
 * servidor nunca respondió (sin internet), o el código explotó antes de enviar.
 * Si cada componente tuviera que distinguir esos casos a mano, la lógica de
 * errores se repetiría por todas partes y sería inconsistente.
 *
 * Esta clase traduce todo eso a UNA forma estable que la interfaz sabe leer.
 */
export class ApiError extends Error {
  /** Código HTTP. `0` cuando no hubo respuesta del servidor (fallo de red). */
  readonly status: number

  /** Código de negocio que envía el backend, si lo hay (ej: 'DUPLICATE_CUSTOMER'). */
  readonly code: string | undefined

  /** Errores por campo, para pintarlos junto a cada input del formulario. */
  readonly fieldErrors: Record<string, string[]> | undefined

  /** Respuesta cruda del servidor. Útil al depurar. */
  readonly data: unknown

  constructor(params: {
    message: string
    status: number
    code?: string | undefined
    fieldErrors?: Record<string, string[]> | undefined
    data?: unknown
  }) {
    super(params.message)
    this.name = 'ApiError'
    this.status = params.status
    this.code = params.code
    this.fieldErrors = params.fieldErrors
    this.data = params.data
  }

  /** Sin conexión, servidor caído o timeout. */
  get isNetworkError(): boolean {
    return this.status === 0
  }

  /** Sesión ausente o expirada. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /** Autenticado, pero sin permiso para esta acción. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  /** El recurso no existe. */
  get isNotFound(): boolean {
    return this.status === 404
  }

  /** Datos rechazados por el backend (formulario inválido). */
  get isValidationError(): boolean {
    return this.status === 422 || this.status === 400
  }

  /** Falla del servidor. Reintentar puede tener sentido. */
  get isServerError(): boolean {
    return this.status >= 500
  }
}

/**
 * Forma esperada del cuerpo de error del backend.
 *
 * 👉 PENDIENTE: ajustar cuando se conozca el contrato real del backend.
 * Es lo único que hay que tocar aquí — el resto del proyecto ya trabaja
 * contra `ApiError`, no contra el formato del servidor.
 */
interface ErrorBody {
  message?: string
  error?: unknown
  code?: string
  errors?: Record<string, string[]>
}

/** Mensajes por defecto, en español y entendibles por un usuario no técnico. */
function defaultMessage(status: number): string {
  if (status === 0) return 'No se pudo conectar con el servidor. Revisa tu conexión a internet.'
  if (status === 401) return 'Tu sesión expiró. Vuelve a iniciar sesión.'
  if (status === 403) return 'No tienes permiso para realizar esta acción.'
  if (status === 404) return 'No se encontró el recurso solicitado.'
  if (status === 409) return 'El registro ya existe o fue modificado por otra persona.'
  if (status === 422 || status === 400) return 'Algunos datos no son válidos. Revísalos.'
  if (status === 429) return 'Demasiadas peticiones. Espera un momento e intenta de nuevo.'
  if (status >= 500) return 'Ocurrió un error en el servidor. Intenta de nuevo en unos minutos.'
  return 'Ocurrió un error inesperado.'
}

/** Convierte cualquier excepción en un ApiError predecible. */
export function normalizeError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  if (axios.isAxiosError<ErrorBody>(error)) {
    const status = error.response?.status ?? 0
    const body = error.response?.data

    return new ApiError({
      message:
        (typeof body?.error === 'object' && body.error !== null
          ? (body.error as { message?: string }).message
          : undefined) ??
        body?.message ??
        (typeof body?.error === 'string' ? body.error : undefined) ??
        defaultMessage(status),
      status,
      code:
        (typeof body?.error === 'object' && body.error !== null
          ? (body.error as { code?: string }).code
          : undefined) ?? body?.code,
      fieldErrors:
        (typeof body?.error === 'object' && body.error !== null
          ? (body.error as { details?: Record<string, string[]> }).details
          : undefined) ?? body?.errors,
      data: body,
    })
  }

  return new ApiError({
    message: error instanceof Error ? error.message : 'Ocurrió un error inesperado.',
    status: 0,
    data: error,
  })
}
